import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { BestOfferBadge } from '../../styles'
import { compactMoney, themeBreakpointUp, themeColor } from '@/utils'
import { Breakpoint, ThemeColor } from '@/enums'
import { useUpdateRecommendationOptions } from '@/services'
import {
  RadioCardState,
  RadioCard,
} from '@/pages/RecommendationPage/components/RadioCard/RadioCard'
import { useSegment } from '@/modules'
import { AdaptiveTooltip } from '@surein/ui'
import { Info } from 'lucide-react'
import { AdvisorTip } from '@/components/molecules/AdvisorTip/AdvisorTip'
import { useSnackbar } from '@/providers'
import { SupportedCustomizationSearchCriteriaKeys } from '@/types/recommendation'

enum OperatorKey {
  AT_LEAST = 'atLeast',
  UP_TO = 'upTo',
}

const OPERATOR_KEYS = {
  '>=': OperatorKey.AT_LEAST,
  '<=': OperatorKey.UP_TO,
}

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RadioCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${themeBreakpointUp(Breakpoint.tab)} {
    flex-direction: row;
  }
`

const StyledBestOfferBadge = styled(BestOfferBadge)`
  width: fit-content;
  background: ${themeColor(ThemeColor.info3)};
`
const RecommendedWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const InfoIconWrapper = styled.div`
  cursor: pointer;
`

interface Props {
  options: {
    value: string
    isRecommended: boolean
  }[]
  initialValue: string
  recommendationId: string
  optionKey: SupportedCustomizationSearchCriteriaKeys
}

const getAmountFromCustomizationKey = (customizationKey: string) => {
  return customizationKey.split(':')[1]
}

const generateAdvisorTipKeyMessage = ({
  optionKey,
  options,
  value,
}: {
  optionKey: SupportedCustomizationSearchCriteriaKeys
  options: {
    value: string
    isRecommended: boolean
  }[]
  value: string
}) => {
  const allAmounts = options.map((item) => parseInt(item.value.split(':')[1]))
  const selectedAmount = parseInt(
    getAmountFromCustomizationKey(
      options.find((item) => item.value === value)?.value || '',
    ) || '',
  )

  if (optionKey === SupportedCustomizationSearchCriteriaKeys.AMOUNT_INSURED) {
    const recommendedAmount = parseInt(
      getAmountFromCustomizationKey(
        options.find((item) => !!item.isRecommended)!.value,
      ) || '',
    )
    return selectedAmount > recommendedAmount
      ? 'higherThanRecommendedWarningMessage'
      : 'lowerThanRecommendedWarningMessage'
  } else if (
    optionKey === SupportedCustomizationSearchCriteriaKeys.DEDUCTIBLES
  ) {
    const highestAmount = Math.max(...allAmounts)
    return selectedAmount === highestAmount
      ? 'farHigherThanRecommendedWarningMessage'
      : 'aBitHigherThanRecommendedWarningMessage'
  }

  return ''
}

export const RecommendationRadio = ({
  options,
  initialValue,
  recommendationId,
  optionKey,
}: Props) => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()

  const [value, setValue] = useState(initialValue)
  const isActiveElementRecommended = options.find(
    (item) => item.value === value,
  )?.isRecommended
  const recommendedValueKey = options.find((item) => !!item.isRecommended)!
    .value
  const analytics = useSegment()

  const updateRecommendationOptionsMutation = useUpdateRecommendationOptions(
    recommendationId,
  )
  const advisorTipMessage = t(
    `recommendations.customization.${optionKey}.${generateAdvisorTipKeyMessage({
      optionKey,
      options,
      value,
    })}`,
    {
      amount: compactMoney(parseInt(getAmountFromCustomizationKey(value))),
    },
  )

  const recommendedValue = options.find((item) => !!item.isRecommended)

  const recommendedInfoMessage = recommendedValue
    ? t(`recommendations.customization.${optionKey}.recommendedInfoMessage`, {
        amount: compactMoney(
          parseInt(getAmountFromCustomizationKey(recommendedValue.value)),
        ),
      })
    : ''

  const generateCardLabel = useCallback((optionValue: string) => {
    if (!optionValue) return ''
    const [operator, amount] = optionValue.split(':')
    const operatorKey = OPERATOR_KEYS[operator as keyof typeof OPERATOR_KEYS]
    const finalAmount = compactMoney(parseInt(amount))
    let finalLabel = finalAmount
    if (operatorKey === OperatorKey.AT_LEAST) {
      finalLabel = t('recommendations.customization.atLeast', {
        amount: finalAmount,
      })
    } else if (operatorKey === OperatorKey.UP_TO) {
      finalLabel = t('recommendations.customization.upTo', {
        amount: finalAmount,
      })
    }

    return finalLabel
  }, [])

  const handleOnClick = useCallback(
    async (newValue: string) => {
      const oldValue = value
      setValue(newValue)
      try {
        await updateRecommendationOptionsMutation.mutateAsync({
          recommendationId,
          [optionKey]: newValue,
        })
      } catch {
        setValue(oldValue)
        snackbar.error(t('snackbars.error.generalMsg'))
      }

      if (newValue === recommendedValueKey) {
        await analytics.track('recommendation_recommended_state', {
          optionKey,
        })
      }

      if (optionKey === SupportedCustomizationSearchCriteriaKeys.DEDUCTIBLES) {
        await analytics.track('recommendation_deductible', {
          value: newValue,
        })
      } else if (
        optionKey === SupportedCustomizationSearchCriteriaKeys.AMOUNT_INSURED
      ) {
        await analytics.track('recommendation_insurance_sum', {
          value: newValue,
        })
      }
    },
    [updateRecommendationOptionsMutation, recommendationId, optionKey],
  )

  const handleTooltipClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  return (
    <ComponentWrapper>
      <RadioCardsContainer>
        {options.map((option) => {
          const isActive = value === option.value
          const radioCardState = !isActive
            ? RadioCardState.INACTIVE
            : option.isRecommended
            ? RadioCardState.ACTIVE
            : RadioCardState.ACTIVE_WARNING
          return (
            <RadioCard
              key={option.value}
              state={radioCardState}
              label={generateCardLabel(option.value)}
              onClick={() => handleOnClick(option.value)}
            >
              {option.isRecommended ? (
                <RecommendedWrapper>
                  <StyledBestOfferBadge>
                    {t('recommendations.customization.recommendedBadgeLabel')}
                  </StyledBestOfferBadge>
                  {recommendedInfoMessage && (
                    <AdaptiveTooltip content={recommendedInfoMessage}>
                      <InfoIconWrapper onClick={handleTooltipClick}>
                        <Info height={24} />
                      </InfoIconWrapper>
                    </AdaptiveTooltip>
                  )}
                </RecommendedWrapper>
              ) : null}
            </RadioCard>
          )
        })}
      </RadioCardsContainer>
      {!isActiveElementRecommended ? (
        <div>
          <AdvisorTip
            title={t(
              'recommendations.customization.recommendationWarningTitle',
            )}
            message={advisorTipMessage}
          />
        </div>
      ) : null}
    </ComponentWrapper>
  )
}
